import React, { useCallback, useEffect } from 'react'
import { LinearProgress } from '@material-ui/core'
import { useGetList } from 'ra-core'

import { API_ENDPOINTS } from 'src/api'
import ReferencedInput from 'src/components/inputs/Referenced/ReferencedInput'
import useErrorFetch from 'src/hooks/useErrorFetch'
import { TReference } from 'src/types'
import { TUseReferenceSelectionParam } from 'src/components/inputs/Referenced/useReferenceSelection'
import { TEntityUntagged } from '@obvious.tech/constellation/Entity'

const REFERENCE: TReference<string, 'Agent'> = {
  target: 'Agent',
  targetLabelKeyPath: 'firstname',
}

const AgentEntityIdInput = ({
  name,
  onChange,
  value,
  record,
  property,
  ...rest
}): JSX.Element => {
  const fetchError = useErrorFetch()

  const {
    data: passports,
    loading,
    error,
  } = useGetList<TEntityUntagged<'Passport'>>(API_ENDPOINTS.PASSPORT, {} as any)

  useEffect(() => {
    if (!error) return

    void fetchError(error)
  }, [error, fetchError])

  const getAgentPassportUsername = useCallback(
    (passportId: string | null): string | null => {
      if (typeof passportId !== 'string') return null

      const passport: TEntityUntagged<'Passport'> | undefined =
        passports[passportId]
      return passport?.username
    },
    [passports],
  )

  const getOptionLabel: NonNullable<
    TUseReferenceSelectionParam<string, 'Agent'>['getOptionLabel']
  > = useCallback(
    ({ entity: agent, id }) => {
      const username = getAgentPassportUsername(agent?.passportId ?? null)
      const fullname =
        typeof agent?.firstname === 'string' ||
        typeof agent?.lastname === 'string'
          ? [agent?.lastname, agent?.firstname].filter(Boolean).join(' ')
          : null

      const label = [
        username,
        typeof fullname === 'string' ? `( ${fullname} )` : null,
      ]
        .filter(Boolean)
        .join(' ')

      return [label, id].filter(Boolean).join(', ')
    },
    [getAgentPassportUsername],
  )

  if (loading) return <LinearProgress />

  return (
    <ReferencedInput<string, 'Agent'>
      name={name}
      {...rest}
      onChange={onChange}
      value={value}
      record={record}
      property={{
        ...property,
        reference: REFERENCE,
      }}
      getOptionLabel={getOptionLabel}
    />
  )
}

export default AgentEntityIdInput
